import { useI18nFormatting } from 'hooks/use-i18n-formatting';

export type DateTextProps = {
  /** Date to be displayed */
  date: number | string | Date;
  /**
   * Format string to be used for formatting the date to be displayed.
   *
   * @remarks Supports localized formats using dayjs format tokens.
   * Now automatically uses the user's current locale (en-CA, fr-CA, etc.)
   *
   * @see https://day.js.org/docs/en/display/format
   * @see https://day.js.org/docs/en/display/format#list-of-localized-formats
   * 
   * @example
   * template="MMM DD, YYYY" // "Oct 15, 2025" (en-CA) or "oct. 15, 2025" (fr-CA)
   * template="ddd. MMM D" // "Wed. Oct 15" (en-CA) or "mer. oct. 15" (fr-CA)
   */
  template?: string;
  /**
   * Function to render the formatted date.
   *
   * @param stringDate - The formatted date string.
   * @returns A JSX element to be rendered. If not provided, the formatted date will be rendered wrapped in a span.
   */
  render?: (stringDate: string) => JSX.Element;
};

/**
 * DateText component that displays formatted dates with automatic locale support.
 * 
 * Now uses i18n formatting to automatically format dates according to the user's locale.
 * Month names, day names, and formats will be localized (e.g., "October" vs "octobre").
 * 
 * @example
 * // English Canada (en-CA):
 * <DateText date={new Date()} template="MMM DD, YYYY" /> // "Oct 15, 2025"
 * 
 * // French Canada (fr-CA):
 * <DateText date={new Date()} template="MMM DD, YYYY" /> // "oct. 15, 2025"
 */
export default function DateText({ date, template, render }: DateTextProps) {
  const { formatDateWithTemplate } = useI18nFormatting();
  const formattedDate = formatDateWithTemplate(date, template || 'MMM DD, YYYY');
  return render?.(formattedDate) || <span>{formattedDate}</span>;
}
